'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';

import { getItineraries } from '../../../../../core/Api/Front/Resources';
import { getAgencyTripReviews } from '../../../../../core/Api/Front/Resources/tripReview';
import { logger } from '../../../../../core/Log/logger';
import { usePagination } from '../../../../../core/Pagination/Pagination';
import {
    useTrackEventAmplitude,
    useTrackPageViewAmplitude,
} from '../../../../../core/Tracking/Amplitude/Amplitude';
import { mapItineraryCardTagsToTotalCounts } from '../../../../../resources/itinerary/domain/operations/getAmplitudeTags';
import { TripReviewListPaginatedGridSection } from '../../../../../shared/components/TripReviewListPaginatedGridSection/TripReviewListPaginatedGridSection';
import { SortValue } from '../../../../../shared/components/TripReviewSort/Sort';
import { useLocale } from '../../../../../shared/hooks/useLocale';
import { FetchItinerariesFilter } from '../../../../api/front/itineraries/route';
import {
    FetchSortTripReviews,
    FetchFiltersTripReviews,
} from '../../../../api/front/trip-reviews/route';

import { Agencies } from './components/Agencies';
import { AgencyFooter } from './components/AgencyFooter';
import { AgencyHeader } from './components/AgencyHeader';
import { Engagement } from './components/Engagement';
import { ItinerariesGrid } from './components/ItinerariesGrid';
import { MembersOfAgency } from './components/MembersOfAgency';
import { RecommendedItinerary } from './components/RecommendedItinerary';

import type { transformAgencyPageData } from './shared/utilities/transformAgencyPageData';

import style from './PageView.module.css';

type PageViewProps = Pick<
    ReturnType<typeof transformAgencyPageData>,
    'destination' | 'agency' | 'recommendedItinerary' | 'itineraries' | 'tripReviews' | 'agencies'
>;

export function PageView({
    destination,
    agency,
    recommendedItinerary,
    itineraries,
    tripReviews,
    agencies,
}: PageViewProps) {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { trackEvent } = useTrackEventAmplitude();

    const [sort, setSort] = useState<SortValue>('default');
    const [selectedFilters, setSelectedFilters] = useState<Array<number>>([]);

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPageLocalAgent({
            ...defaultProperties,
            agency_better_trip_index: agency.betterTripIndex,
            agency_id: agency.id,
            agency_slug: agency.slug,
            has_trip_form_available: agency.isAvailable,
            has_video_content: !!agency.presentationVideoEmbedUrl,
            nb_agents_displayed: agency.team.length > 1 ? agency.team.length : 0,
            rating: agency.tripReviewsAverageRating?.rating.value,
            destination_id: destination.id,
            destination_slug: destination.slug,
            nb_tags_list: mapItineraryCardTagsToTotalCounts(itineraries.items),
            is_refund_eligible: false,
        });
    });

    async function paginationFetcher({
        itemsPerPage,
        page,
    }: {
        itemsPerPage: number;
        page: number;
    }) {
        return getItineraries({
            locale,
            page,
            perPage: itemsPerPage,
            filters: [
                { key: 'agencyId', value: agency.id },
                { key: 'destinationId', value: destination.id },
                ...(recommendedItinerary
                    ? [
                          {
                              key: '!itineraryId',
                              value: recommendedItinerary.id,
                          } as FetchItinerariesFilter,
                      ]
                    : []),
            ],
        });
    }

    const itinerariesPagination = usePagination({
        pagination: itineraries.pagination,
        items: itineraries.items,
        fetcher: paginationFetcher,
    });

    const tripReviewsPagination = usePagination({
        items: tripReviews.items,
        pagination: tripReviews.pagination,
        fetcher: ({ itemsPerPage, page }) => {
            return getAgencyTripReviews({
                agencyId: agency.id,
                locale,
                page,
                perPage: itemsPerPage,
                sort: getSortValue(sort),
                filters: getFiltersValue(selectedFilters),
            });
        },
    });

    const itinerariesCount = itinerariesPagination.allItems().length;

    function handleSeeMore() {
        itinerariesPagination.goToNextPage().catch((error) => {
            logger.error('Itineraries fetch next page of itineraries failed', {
                routeName: 'agency',
                locale,
                pagination: {
                    firstPage: itinerariesPagination.firstPage,
                    previousPage: itinerariesPagination.previousPage,
                    currentPage: itinerariesPagination.currentPage,
                    nextPage: itinerariesPagination.nextPage,
                    lastPage: itinerariesPagination.lastPage,
                },
                destination,
                error,
            });
        });
        trackEvent((ampli, defaultProperties) => {
            ampli.moreItinerariesClicked({
                ...defaultProperties,
                block: 'itineraries',
            });
        });
    }

    return (
        <>
            <AgencyHeader destination={destination} agency={agency} />

            {recommendedItinerary || itineraries.items.length ? (
                <section className="layout-with-centered-content">
                    {recommendedItinerary ? (
                        <RecommendedItinerary
                            recommendedItinerary={recommendedItinerary}
                            destination={destination}
                            agency={agency}
                        />
                    ) : null}

                    {itineraries ? (
                        <ItinerariesGrid
                            isAnyPageLoading={itinerariesPagination.isAnyPageLoading()}
                            hasNextPage={
                                (itinerariesPagination.nextPage &&
                                    !itinerariesPagination.isAnyPageLoading()) ||
                                false
                            }
                            destination={destination}
                            itineraries={itinerariesPagination.allItems()}
                            itinerariesCount={itinerariesCount}
                            handleSeeMore={handleSeeMore}
                            agency={agency}
                        />
                    ) : null}
                </section>
            ) : null}

            {agency.team.length <= 1 && !agency.presentationVideoEmbedUrl ? null : (
                <MembersOfAgency agency={agency} />
            )}

            <section className="layout-with-centered-content">
                {agency.responsibleTourismDescription ? (
                    <Engagement agency={agency} destination={destination} />
                ) : null}

                <div className={`main-container ${style.reviewsSection}`} id="reviews">
                    {agency.tripReviewsAverageRating ? (
                        <TripReviewListPaginatedGridSection
                            averageRating={agency.tripReviewsAverageRating}
                            destination={destination}
                            title={{
                                as: 'h2',
                                scale: 'primary-m-extra-bold',
                                children: formatMessage({
                                    id: 'page.agency.tripReviewListSection.title',
                                }),
                            }}
                            tripReviewsCountByRating={tripReviews.countByRatings}
                            pagination={tripReviewsPagination}
                            displayAgencyCta={false}
                            selectedSort={sort}
                            selectedFilters={selectedFilters}
                            onSortSelected={setSort}
                            onFiltersSelected={setSelectedFilters}
                            trackingInformation={{
                                agencyReviewsSeen: {
                                    agency_id: agency.id,
                                    agency_slug: agency.slug,
                                    agency_better_trip_index: agency.betterTripIndex,
                                },
                                agencyReviewsMore: {
                                    agency_id: agency.id,
                                    agency_slug: agency.slug,
                                    agency_better_trip_index: agency.betterTripIndex,
                                },
                                agencyReviewsFiltered: {
                                    agency_id: agency.id,
                                    agency_slug: agency.slug,
                                    agency_better_trip_index: agency.betterTripIndex,
                                },
                            }}
                        />
                    ) : (
                        <>
                            {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                            <h2 className="font-l-extrabold">
                                {formatMessage({
                                    id: 'page.agency.tripReviewListSection.title',
                                })}
                            </h2>
                            {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                            <p className={`font-body-bold ${style.reviewsEmptyItem}`}>
                                {formatMessage({
                                    id: 'page.agency.tripReviewList.empty.subtitle',
                                })}
                            </p>
                            {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                            <p className={`font-body-regular ${style.reviewsEmptyItem}`}>
                                {formatMessage({
                                    id: 'page.agency.tripReviewList.empty.content',
                                })}
                            </p>
                        </>
                    )}
                </div>
            </section>

            {agencies.length > 0 ? (
                <Agencies destination={destination} agencies={agencies} />
            ) : null}

            <AgencyFooter />
        </>
    );
}

const getSortValue = (sort: SortValue): FetchSortTripReviews | undefined => {
    switch (sort) {
        case 'ratingDesc':
            return {
                key: 'rating',
                value: 'DESC',
            };
        case 'ratingAsc':
            return {
                key: 'rating',
                value: 'ASC',
            };
        default:
            return undefined;
    }
};

const getFiltersValue = (filters: Array<number>): Array<FetchFiltersTripReviews> => {
    return [
        {
            key: 'rating',
            value: filters,
        },
    ];
};
